
import { useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import TestCreatorContainer from "@/components/test-creator/TestCreatorContainer";
import { useTestCreator } from "@/hooks/useTestCreator";
import { useProfile } from "@/contexts/ProfileContext";
import { useAuth } from "@/contexts/AuthContext";
import { MainNav } from "@/components/MainNav";
import { Link } from "react-router-dom";

import { useIsMobile } from "@/hooks/use-mobile";
import { MobileBottomNav } from "@/components/nav/MobileBottomNav";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { showErrorToast } from "@/utils/toast-utils";
import { SuccessScreen } from "@/components/test-creator/screens/SuccessScreen";

export default function TestCreator() {
  const { testId, successId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useProfile();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const { subscription, isLoading: isLoadingSubscription } = useSubscription();
  
  // This ref persists across re-renders but doesn't cause re-renders when it changes
  const subscriptionCheckPerformed = useRef(false);

  // Redirect candidates to dashboard - they shouldn't access test creation
  useEffect(() => {
    if (profile && profile.role === 'candidate') {
      console.log('[TestCreator] Redirecting candidate to dashboard');
      navigate('/dashboard');
      return;
    }
  }, [profile, navigate]);
  
  // Get job details from location state
  const jobDetails = location.state?.jobDetails;
  const preGeneratedQuestions = location.state?.preGeneratedQuestions;
  const skipToReview = location.state?.skipToReview;
  
  // Use test details from location state if available, otherwise use testId
  const testCreator = useTestCreator(jobDetails);
  
  useEffect(() => {
    // Store test initial data for pending creation after signup if needed
    if (jobDetails) {
      console.log('Storing job details in session storage for potential future use');
      // Changed from pendingJobDetails to pendingTestDetails to maintain consistency
      sessionStorage.setItem('pendingTestDetails', JSON.stringify(jobDetails));
    }
  }, [jobDetails]);

  
  const handleTestCreated = (id: string) => {
    console.log('Test created with ID:', id);
    // Navigate to success page with the test ID
    navigate(`/test-creator/success/${id}`, { replace: true });
  };
  
  // Mobile navigation for the TestCreator page
  const renderMobileTopNav = () => {
    if (isMobile && user) {
      return (
        <nav className="fixed top-0 left-0 right-0 z-[100] bg-background border-b border-border">
          <div className="container mx-auto px-4">
            <div className="flex h-16 items-center justify-between">
              <Link to="/" className="flex items-center space-x-2">
                <span className="text-2xl font-bold text-foreground tracking-tight">
                  Testera
                </span>
              </Link>
            </div>
          </div>
        </nav>
      );
    }
    return <MainNav />;
  };

  // If still loading subscription data, show nothing yet to prevent flashes
  if (isLoadingSubscription && !testId) {
    return <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-foreground"></div>
    </div>;
  }

  // If we have a successId, show the success screen
  if (successId) {
    return (
      <div className="min-h-screen flex flex-col w-full">
        {renderMobileTopNav()}
        <div className="w-full pt-16 mx-auto bg-background">
          <div className="container max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <SuccessScreen testId={successId} />
          </div>
        </div>
        {isMobile && user && <MobileBottomNav />}
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex flex-col w-full">
      {renderMobileTopNav()}
      <div className="w-full pt-16 mx-auto bg-background">
        <div className="container max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="mt-12">
            <TestCreatorContainer 
              initialDetails={jobDetails}
              initialQuestions={preGeneratedQuestions || location.state?.initialQuestions || []}
              onTestCreated={handleTestCreated}
              startAtQuestionMaker={Boolean(location.state?.startAtQuestionMaker)}
              skipToReview={skipToReview}
            />
          </div>
        </div>
      </div>
      {isMobile && user && <MobileBottomNav />}
    </div>
  );
}
